import { Stack, Tab, Tabs, Typography } from '@mui/material';
import SearchInput from 'components/shared/SearchInput';
import { ROUTES } from 'constants/routes';
import useIsAuditEnabled from 'hooks/useIsAuditEnabled';
import React, { useCallback, useEffect, useState } from 'react';
import { AuditType, IAuditEvent } from 'types/api';
import { useDebounceCallback } from 'usehooks-ts';
import { useNav } from 'utils/navigation';
import AuditEventDrawer from './AuditEventDrawer';
import AuditEvents from './AuditEvents';

const SEARCH_DEBOUNCE_MS = 400;

const AuditPage: React.FC = () => {
	const nav = useNav();
	const { enabled, ready } = useIsAuditEnabled();
	const [type, setType] = useState(AuditType.Sso);
	const [text, setText] = useState('');
	const setTextDebounced = useDebounceCallback(setText, SEARCH_DEBOUNCE_MS);
	const [selectedEvent, setSelectedEvent] = useState<IAuditEvent | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);

	// A bookmarked URL reaches this page without passing the home button or sidebar entry that hide it.
	useEffect(() => {
		if (ready && !enabled) {
			nav(ROUTES.home);
		}
	}, [ready, enabled, nav]);

	const openEvent = useCallback((event: IAuditEvent) => {
		setSelectedEvent(event);
		setDrawerOpen(true);
	}, []);

	if (!enabled) {
		return null;
	}

	return (
		<Stack gap={4}>
			<Stack gap={0.5}>
				<Typography variant="h2">{intl.tr('general.auditLog')}</Typography>
				<Typography variant="body1">{intl.tr('audit.description')}</Typography>
			</Stack>
			<Stack gap={3}>
				<Tabs value={type} onChange={(_, value: AuditType) => setType(value)}>
					<Tab
						value={AuditType.Sso}
						label={intl.tr('audit.tabs.sso')}
						data-testid="audit-tab-sso"
					/>
					<Tab
						value={AuditType.Scim}
						label={intl.tr('audit.tabs.scim')}
						data-testid="audit-tab-scim"
					/>
				</Tabs>
				<SearchInput
					data-testid="audit-search"
					sx={{ maxWidth: 528, height: 50 }}
					onSearchChange={(value) => setTextDebounced(value.trim())}
				/>
				<AuditEvents type={type} text={text} onEventClick={openEvent} />
			</Stack>
			<AuditEventDrawer
				event={selectedEvent}
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
			/>
		</Stack>
	);
};

export default AuditPage;
